// src/api/muestraApi.js
import axios from "axios";
import { formatMuestraDateForAPI } from "../../utils/dateUtils";
import { normalizeMuestraQrCode } from "./muestraQrCode";

const API_URL = process.env.REACT_APP_API_URL;

const MUESTRA_DATE_FIELDS = [
  "FECHA_PETICION",
  "FECHA_PREVISTA",
  "FECHA_GET_SUPPLIER",
  "FECHA_ENVIO",
  "FECHA_RECEPCION",
  "FECHA_PREVISTA_FABRICA",
];

/** Normaliza fechas a yyyy-MM-dd antes de enviar al API (evita ISO con zona en MSSQL). */
export const serializeMuestraPayload = (data = {}) => {
  if (!data || typeof data !== "object") return data;
  const out = { ...data };
  for (const key of MUESTRA_DATE_FIELDS) {
    if (key in out) {
      out[key] = formatMuestraDateForAPI(out[key]);
    }
  }
  return out;
};

export const addMuestra = async (muestraData) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    const response = await axios.post(
      `${API_URL}/api/muestras`,
      serializeMuestraPayload(muestraData),
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error al añadir muestra:", error);
    throw error;
  }
};

export const deleteMuestra = async (muestraId) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    await axios.delete(`${API_URL}/api/muestras/${muestraId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (error) {
    console.error("Error al eliminar la muestra:", error);
    throw error;
  }
};

export const getAllMuestras = async () => {
  const token = localStorage.getItem("token");
  try {
    const response = await axios.get(`${API_URL}/api/muestras`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.map((muestra) => ({
      ...muestra,
      ID_Marca: muestra.ID_Marca || null, // Asegurar que siempre exista
    }));
  } catch (error) {
    console.error("❌ Error al obtener muestras:", error);
    throw error;
  }
};

export const getMuestraEstados = async () => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    const response = await axios.get(`${API_URL}/api/muestra-estados`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error al obtener estados de muestra:", error);
    throw error;
  }
};

export const getTallas = async () => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    const response = await axios.get(`${API_URL}/api/tallas`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error al obtener tallas:", error);
    throw error;
  }
};

export const getColores = async () => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    const response = await axios.get(`${API_URL}/api/colores`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error al obtener colores:", error);
    throw error;
  }
};

export const getColoresMuestras = async () => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    const response = await axios.get(`${API_URL}/api/colores-muestras`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error al obtener colores de muestras:", error);
    throw error;
  }
};

export const updateColorMuestra = async (id, data) => {
  const token = localStorage.getItem("token");
  try {
    const response = await axios.put(
      `${API_URL}/api/colores-muestras/${id}`,
      data,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error al actualizar color de muestra:", error);
    throw error;
  }
};

export const getProveedores = async () => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    const response = await axios.get(`${API_URL}/api/proveedores`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error al obtener proveedores:", error);
    throw error;
  }
};

export const getProductMuestras = async (productId) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    const response = await axios.get(`${API_URL}/api/muestras/${productId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error al obtener muestras del producto:", error);
    throw error;
  }
};

export const updateMuestra = async (muestraId, muestraData) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    const response = await axios.put(
      `${API_URL}/api/muestras/${muestraId}`,
      serializeMuestraPayload(muestraData),
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return response.data;
  } catch (error) {
    console.error("Error al actualizar la muestra:", error);
    throw error;
  }
};

export const patchMuestraFotosExport = async (muestraId, fotosExport) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    const response = await axios.patch(
      `${API_URL}/api/muestras/${muestraId}/fotos-export`,
      fotosExport || { order: [], front: null, back: null },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error al guardar fotos para PDF:", error);
    throw error;
  }
};

export const prepareMuestraPhoto = async (
  muestraId,
  { path, engine = "garment", role, extraPrompt } = {},
) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    const response = await axios.post(
      `${API_URL}/api/muestras/${muestraId}/prepare-photo`,
      {
        path,
        engine,
        ...(role ? { role } : {}),
        ...(extraPrompt ? { extraPrompt } : {}),
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: 330000,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error al preparar foto de muestra:", error);
    throw error;
  }
};

export const prepareMuestraPhotos = async (
  muestraId,
  { paths, engine = "gpt-image", extraPrompt } = {},
) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    const response = await axios.post(
      `${API_URL}/api/muestras/${muestraId}/prepare-photos`,
      {
        ...(paths?.length ? { paths } : {}),
        engine,
        ...(extraPrompt ? { extraPrompt } : {}),
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: 330000,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error al preparar fotos de muestra:", error);
    throw error;
  }
};

export const getMuestraById = async (muestraId) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    const response = await axios.get(
      `${API_URL}/api/muestras/detail/${muestraId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error al obtener la muestra:", error);
    throw error;
  }
};

export const getMuestrasTipo = async () => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/muestrasTipo`,
    );
    return response.data;
  } catch (error) {
    console.error("Error al obtener tipos de muestra:", error);
    throw error;
  }
};

export const getMuestrasEstado = async () => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/muestrasEstado`,
    );
    return response.data;
  } catch (error) {
    console.error("Error al obtener Estado de muestra:", error);
    throw error;
  }
};

export const getMuestrasTallas = async () => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/muestrasTallas`,
    );
    return response.data;
  } catch (error) {
    console.error("Error al obtener Tallas de Muestra:", error);
    throw error;
  }
};

export const getMuestrasColores = async () => getColoresMuestras();

export const getMuestrasProveedores = async () => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/muestrasProveedores`,
    );
    return response.data;
  } catch (error) {
    console.error("Error al obtener Proveedores de Muestra:", error);
    throw error;
  }
};

export const addEscandallo = async (escandalloData) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    const response = await axios.post(
      `${API_URL}/api/muestras-escandallo`,
      escandalloData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error al añadir escandallo:", error.response || error);
    throw error;
  }
};

export const getEscandallosByMuestraId = async (muestraId) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    const response = await axios.get(
      `${API_URL}/api/muestras-escandallo/${muestraId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error al obtener escandallos:", error);
    throw error;
  }
};

export const getEscandalloGrupos = async () => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    const response = await axios.get(`${API_URL}/api/escandallo-grupos`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error al obtener grupos de escandallo:", error);
    throw error;
  }
};

export const getEscandalloSubgruposMuestras = async (idGrupo) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    const response = await axios.get(
      `${API_URL}/api/escandallo-subgrupos-muestras/${idGrupo}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error al obtener subgrupos de escandallo:", error);
    throw error;
  }
};

export const updateEscandallo = async (escandalloId, escandalloData) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    const response = await axios.put(
      `${API_URL}/api/muestras-escandallo/${escandalloId}`,
      escandalloData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error al actualizar el escandallo:", error);
    throw error;
  }
};

export const getVistaMuestrasDesigners = async () => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    const response = await axios.get(
      `${API_URL}/api/vista-muestras-designers`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error al obtener datos de la vista de muestras y diseñadores:",
      error,
    );
    throw error;
  }
};

// src/api/muestraApi.js
export const deleteEscandallo = async (escandalloId) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    await axios.delete(`${API_URL}/api/muestras-escandallo/${escandalloId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (error) {
    console.error("Error al eliminar el escandallo:", error);
    throw error;
  }
};

export const getMarcas = async () => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/marcas`,
    );
    return response.data;
  } catch (error) {
    console.error("Error al obtener marcas:", error);
    throw error;
  }
};

export const getMovimientosMuestras = async (ID_MUESTRA) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    const url = ID_MUESTRA
      ? `${process.env.REACT_APP_API_URL}/api/movimientosMuestras?ID_MUESTRA=${ID_MUESTRA}`
      : `${process.env.REACT_APP_API_URL}/api/movimientosMuestras`;

    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error al obtener movimientos Muestras:", error);
    throw error;
  }
};

export const getMovimientosDetallados = async () => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/movimientosMuestras/detallado`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error al obtener movimientos detallados:", error);
    throw error;
  }
};

export const addMovimiento = async (movimientoData) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    const response = await axios.post(
      `${API_URL}/api/movimientosMuestras`,
      movimientoData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return response.data;
  } catch (error) {
    console.error("Error al añadir movimiento:", error.response || error);
    throw error;
  }
};

/* export const updateMovimiento = async (movimientoId, movimientoData) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    const response = await axios.put(
      `${API_URL}/api/movimientosMuestras/${movimientoId}`,
      movimientoData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error al actualizar movimiento:", error);
    throw error;
  }
}; */

export const updateMovimiento = async (movimientoId, movimientoData) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Token no encontrado");

  const payload = {
    ID_Marca: movimientoData.ID_Marca ?? null,
    FECHA_ENVIO: formatMuestraDateForAPI(movimientoData.FECHA_ENVIO),
    NUM_COURIER: movimientoData.NUM_COURIER || null,
    TIPO_DESTINO: movimientoData.TIPO_DESTINO || null,
    ID_CLIENTE: movimientoData.ID_CLIENTE ?? null,
    CODPROV: movimientoData.CODPROV ?? null,
    ID_CONTACTO: movimientoData.ID_CONTACTO ?? null,
    PERSONA_ENVIO: movimientoData.PERSONA_ENVIO || null,
  };

  try {
    const response = await axios.put(
      `${API_URL}/api/movimientosMuestras/${movimientoId}`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  } catch (error) {
    console.error("❌ Error al actualizar movimiento:", error);
    throw error;
  }
};

export const deleteMovimiento = async (movimientoId) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Token no encontrado, por favor inicia sesión");
  }

  try {
    await axios.delete(`${API_URL}/api/movimientosMuestras/${movimientoId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (error) {
    console.error("Error al eliminar movimiento:", error);
    throw error;
  }
};

/** ✅ Obtener historial de una muestra dentro de una maleta */
export const getMuestraHistorial = async (maletaId, muestraId) => {
  const token = localStorage.getItem("token");
  try {
    const response = await axios.get(
      `${API_URL}/api/maletas/${maletaId}/muestras/${muestraId}/historial`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error en getMuestraHistorial:",
      error.response?.data || error,
    );
    throw error;
  }
};

/** ✅ Descargar informe PDF de una maleta */
export const descargarInformeMaleta = async (maletaId) => {
  const token = localStorage.getItem("token");
  try {
    const response = await axios.get(
      `${API_URL}/api/maletas/${maletaId}/informe`,
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      },
    );

    // Crear un enlace para descargar el archivo
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Informe_Maleta_${maletaId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error(
      "❌ Error al descargar informe PDF:",
      error.response?.data || error,
    );
    throw error;
  }
};

export const scanBarcode = async (maletaId, muestraId) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Token no encontrado, por favor inicia sesión");

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/maletas/${maletaId}/muestras`,
      { muestraId },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data;
  } catch (error) {
    console.error("❌ Error en scanBarcode:", error.response?.data || error);
    throw error;
  }
};

export const updateColor = async (id, data) => {
  const token = localStorage.getItem("token");
  try {
    await axios.put(`${API_URL}/api/colores/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (error) {
    console.error("Error al actualizar color:", error);
    throw error;
  }
};

export const addColor = async (data) => {
  const token = localStorage.getItem("token");
  try {
    const response = await axios.post(`${API_URL}/api/colores`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    console.error("Error al crear color:", error);
    throw error;
  }
};

export const getFilesByMuestraId = async (id, category) => {
  const token = localStorage.getItem("token");
  const response = await axios.get(
    `${process.env.REACT_APP_API_URL}/api/files/muestra/${id}`,
    {
      headers: { Authorization: `Bearer ${token}` },
      params: category ? { category } : undefined,
      // Carpetas vacías / sin carpeta: el API debe responder 200 [] (legacy: 404 [])
      validateStatus: (status) => status === 200 || status === 404,
    },
  );
  if (response.status === 404) {
    return [];
  }
  return Array.isArray(response.data) ? response.data : [];
};

/** ✅ Obtener datos de una muestra a partir de un QR escaneado */
export const getMuestraByQrCode = async (qrCodeText) => {
  const token = localStorage.getItem("token");
  const code = normalizeMuestraQrCode(qrCodeText);
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/muestras/qr/${encodeURIComponent(
        code,
      )}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error en getMuestraByQrCode:",
      error.response?.data || error,
    );
    throw error;
  }
};

/** ✅ Registrar movimiento con cámara (ubicación) */
export const registrarMovimientoUbicacion = async ({
  muestraId,
  maletaId,
  ubicacion,
  fecha,
  marcaId,
}) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Token no encontrado");

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/movimientosMuestras/ubicacion`,
      { muestraId, maletaId, ubicacion, fecha, marcaId },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data;
  } catch (error) {
    console.error("❌ Error en registrarMovimientoUbicacion:", error);
    throw error;
  }
};

export const getMuestrasTemporadas = async () => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Token no encontrado, por favor inicia sesión");

  const res = await axios.get(`${API_URL}/api/muestras/temporadas`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return res.data;
};

/** List sample + technical attachments available for supplier order email */
export const getOrderEmailAttachments = async (muestraId) => {
  const token = localStorage.getItem("token");
  const response = await axios.get(
    `${process.env.REACT_APP_API_URL}/api/muestras/${muestraId}/order-email-attachments`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  return response.data;
};

/** Send sample order email to supplier */
export const sendMuestraOrderEmail = async (
  muestraId,
  { to, cc, message, technicalFileKeys, attachMode },
) => {
  const token = localStorage.getItem("token");
  const response = await axios.post(
    `${process.env.REACT_APP_API_URL}/api/muestras/${muestraId}/send-order-email`,
    { to, cc, message, technicalFileKeys, attachMode },
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  return response.data;
};

// Subir archivo desde Drawer de Muestras
export const uploadFileToMuestra = async (muestraId, file, category) => {
  const token = localStorage.getItem("token");

  const formData = new FormData();
  formData.append("file", file);
  if (category) {
    formData.append("category", category);
  }

  const res = await axios.post(
    `${process.env.REACT_APP_API_URL}/api/files/muestra/${muestraId}/upload`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: category ? { category } : undefined,
      // Vídeos de reunión pueden ser grandes
      timeout: 10 * 60 * 1000,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    },
  );
  return res.data;
};

/** Copiar fotos/vídeos de una reunión a la parte técnica de la muestra (Ficha Técnica). */
export const importMeetingMediaToMuestra = async (
  muestraId,
  { sessionId, photoIds, category },
) => {
  const token = localStorage.getItem("token");
  const res = await axios.post(
    `${process.env.REACT_APP_API_URL}/api/files/muestra/${muestraId}/import-from-meeting`,
    { sessionId, photoIds, category },
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  return res.data;
};

/** Mover adjunto técnico de una muestra a otra (mismo diseño) */
export const moveTechnicalFileBetweenMuestras = async (
  sourceMuestraId,
  { targetMuestraId, folder, fileName },
) => {
  const token = localStorage.getItem("token");
  const res = await axios.post(
    `${process.env.REACT_APP_API_URL}/api/files/muestra/${sourceMuestraId}/move-technical`,
    { targetMuestraId, folder, fileName },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return res.data;
};

/** Mover adjunto técnico legacy (nivel diseño) → una muestra */
export const moveDesignTechnicalToMuestra = async (
  targetMuestraId,
  { folder, fileName },
) => {
  const token = localStorage.getItem("token");
  const res = await axios.post(
    `${process.env.REACT_APP_API_URL}/api/files/muestra/${targetMuestraId}/receive-design-technical`,
    { folder, fileName },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return res.data;
};

/** Copiar todos los archivos de una muestra a otra (mismo diseño) */
export const copyMuestraFilesFrom = async (
  targetMuestraId,
  sourceMuestraId,
) => {
  const token = localStorage.getItem("token");
  const res = await axios.post(
    `${process.env.REACT_APP_API_URL}/api/files/muestra/${targetMuestraId}/copy-from/${sourceMuestraId}`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return res.data;
};

export const downloadFileFromMuestra = async (
  muestraId,
  fileName,
  category,
) => {
  const token = localStorage.getItem("token");
  const response = await axios.get(
    `${process.env.REACT_APP_API_URL}/api/files/muestra/${muestraId}/download/${encodeURIComponent(fileName)}`,
    {
      headers: { Authorization: `Bearer ${token}` },
      params: category ? { category } : undefined,
      responseType: "blob",
    },
  );

  const mime = response.headers?.["content-type"] || "application/octet-stream";
  const blob =
    response.data instanceof Blob
      ? response.data.type
        ? response.data
        : new Blob([response.data], { type: mime })
      : new Blob([response.data], { type: mime });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// Eliminar archivos subidos desde Muestras
export const deleteFileFromMuestra = async (muestraId, fileName, category) => {
  const token = localStorage.getItem("token");

  await axios.delete(
    `${process.env.REACT_APP_API_URL}/api/files/muestra/${muestraId}/delete/${encodeURIComponent(fileName)}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: category ? { category } : undefined,
    },
  );
};
